import { computePosition, flip, offset, shift, size } from "@floating-ui/dom";
import { clamp } from "lodash-es";

import { MAX_BASE_PER_PIXEL, MIN_BASE_PER_PIXEL } from "../../settings.ts";
import { type GenomeViewerStore, useGenomeViewerStore } from "../../store/store.ts";
import { align } from "../../utils/align.ts";
import { refreshTracks } from "../messages/post-track-messages.ts";
import { refreshMain } from "../render-main.ts";
import { getAllTracksHeight } from "../utils.ts";

export function handleScroll(deltaX: number, deltaY: number, isWheel = false) {
  const state = useGenomeViewerStore.getState();
  const trackGroups = state.trackGroups;
  const allTracksHeight = getAllTracksHeight();

  const scrollUpdate = { scrollX: state.scrollX, scrollY: state.scrollY };

  if (state.asynchronousScrolling) {
    if (state.hoveredGroupId) {
      const activeTrackGroup = trackGroups.get(state.hoveredGroupId)!;
      const deltaBase = deltaX * state.basePerPixel;
      const newBaseShift = activeTrackGroup.graphics.baseShift - deltaBase;

      activeTrackGroup.graphics.baseShift = newBaseShift;

      if (isWheel) {
        scrollUpdate.scrollY = Math.floor(
          Math.max(0, Math.min(state.scrollY + deltaY, allTracksHeight - state.canvas!.height)),
        );
      }
    }
  } else {
    scrollUpdate.scrollX = Math.floor(state.scrollX + deltaX);
    scrollUpdate.scrollY = Math.floor(
      Math.max(0, Math.min(state.scrollY + deltaY, allTracksHeight - state.canvas!.height)),
    );
  }
  useGenomeViewerStore.getState().forceOverviewRender();
  useGenomeViewerStore.setState(scrollUpdate);

  refreshTracks(); // new imageBitmap
  refreshMain(); // move the coordinates as soon as possible
}

export function handleClick(event: PointerEvent, mutableStoreUpdate: Partial<GenomeViewerStore>) {
  const { contextualMenuData } = useGenomeViewerStore.getState();
  mutableStoreUpdate.selectedRange = undefined;

  // Right click action to open the contextual menu
  if ("button" in event && event.button === 2 && contextualMenuData?.id != undefined) {
    const contextualMenu: HTMLDivElement = document.querySelector("#genome-viewer-contextual-menu")!;

    mutableStoreUpdate.showFeaturePopover = true;

    Object.assign(contextualMenu.style, {
      display: "block",
    });

    const virtualEl = {
      getBoundingClientRect() {
        return {
          width: 0,
          height: 0,
          x: event.clientX,
          y: event.clientY,
          left: event.clientX,
          right: event.clientX,
          top: event.clientY,
          bottom: event.clientY,
        };
      },
    };

    computePosition(virtualEl, contextualMenu, {
      placement: "right-start",
      middleware: [
        offset(5),
        flip(),
        shift(),
        size({
          boundary: document.querySelector("#genome-viewer-canvas")!,
          apply({ availableHeight, elements, y }) {
            // Change styles, e.g.
            Object.assign(elements.floating.style, {
              // maxWidth: `${Math.max(0, Math.floor(availableWidth - 10))}px`,
              maxHeight: `${Math.max(0, Math.floor(availableHeight - y - 10))}px`,
            });
          },
        }),
      ],
    }).then(({ x, y }) => {
      Object.assign(contextualMenu.style, {
        top: `${y}px`,
        left: `${x}px`,
      });
    });
  } else {
    // remove selection, ...
    refreshMain();
  }
}

export function handleZoom(zoomFactor: number, centerX: number, alignLeft = false) {
  const state = useGenomeViewerStore.getState();

  const updateStore: Partial<GenomeViewerStore> = {};

  // Calculate new scale while enforcing limits
  const newBasePerPixel = clamp(state.basePerPixel * zoomFactor, MIN_BASE_PER_PIXEL, MAX_BASE_PER_PIXEL);

  // Adjust scrollX to keep the center point at the same position
  const centerCoordinateX = (state.scrollX + centerX) * state.basePerPixel;

  const newScrollX = centerCoordinateX / newBasePerPixel - (alignLeft ? 0 : centerX);

  if (state.basePerPixel !== newBasePerPixel) {
    updateStore.scrollX = newScrollX;
    updateStore.basePerPixel = newBasePerPixel;
    updateStore.hoverData = undefined; // just remove it while zooming, because it's outdated now. Has to move the mouse to trigger it again though.
  }

  if (newBasePerPixel <= MIN_BASE_PER_PIXEL && state.zoomRange) {
    updateStore.scrollX = newScrollX;
  }

  useGenomeViewerStore.getState().forceOverviewRender();
  useGenomeViewerStore.setState(updateStore);
  refreshTracks();
  refreshMain();
}

export async function handleDoubleClick(_event: PointerEvent) {
  const { hoverData, tracks, trackGroups } = useGenomeViewerStore.getState();

  if (hoverData?.id && hoverData.trackId && hoverData.orthologId) {
    const trackGroupId = tracks.get(hoverData.trackId)?.groupId;

    if (trackGroupId) {
      const contigId = trackGroups.get(trackGroupId)?.contigId;

      if (contigId) {
        // If we have hover data, we can use it for the double click action
        align({
          featureId: hoverData.id,
          trackId: hoverData.trackId,
          familyId: hoverData.orthologId,
          trackGroupId,
          contigId,
          start: hoverData.start,
        });
      }
    }
  }
}
